import { put, takeLatest, call } from 'redux-saga/effects'
import fetchFromAPI, { getDefaultAutorizedHeaders } from 'helpers/fetchFromApi'
import UrlHelper from 'helpers/url'
import NoticeHelper from 'helpers/notice'
import {
  API_URL_SITE_LIST,
  API_URL_SITE_TABLE,
  API_URL_SITE_ITEM,
  API_URL_SITE_LIST_BY_SUPER_USER,
  API_URL_SITE_LIST_BY_USER,
  API_URL_DASHBOARD_SITE_TYPES,
} from 'constants/index'
import * as ApiRouter from 'constants/apiRouting'
import { createSiteForm } from 'helpers/site'
import {
  SITE_FETCH_LIST,
  SUCCESS_SITE_LIST,
  CLEAR_SITE_LIST,
  LOAD_SITE_LIST,
  FETCH_SITE_TABLE,
  SUCCESS_SITE_TABLE,
  CLEAR_SITE_TABLE,
  LOAD_SITE_TABLE,
  FETCH_SITE_FORM,
  SUCCESS_SITE_FORM,
  CLEAR_SITE_FORM,
  LOAD_SITE_FORM,
  SITE_CHANGE,
  SITE_DELETE,
  SITE_FETCH_LIST_BY_USER,
  SITE_FETCH_LIST_BY_USER_SUCCESS,
} from './reducer'
import { SHOW_NOTICE } from '../notice'

function* siteFetchList({ payload, promise: { resolve, reject } }) {
  try {
    /**
     * Данную конструкцию предлагаю юзать во всех сагах, которые будут использоваться
     * минимум в 2х и более местах. На основании этой идеи можно создать
     * абстрактную функцию-генератор, в которую можно передавать только необходимые
     * опции, а не дублировать всю эту ленту ниже.
     * isOnlyPromiseReturn - опция, которая позволяет только вернуть промис с результатом запроса или с ошибкой запроса.
     * Обязательные действия (successAction и failureAction) при наличии этой опции игнорируются.
     * loadOnAction - действие, которое будет включать какой то лоадер (не обязательно)
     * loadOffAction - действие, которое будет отключать какой то лоадер (не обязательно)
     * successAction - действие, которое будет принимать данные с  сервера в случае успешного ответа сервера
     * failureAction - действие, которое будет выполнено в случае серверной ошибки
     * successMessage - сообщение, которое необходимо отобразить в случае успешного ответа сервера (не обязательно)
     * failureMessage - сообщение, которое необходимо отобразить в случае ошибки на сервере (не обязательно)
     * data - все остальное, что было передано в payload - обратносовместимо с существующим функционалом
     */
    const {
      isOnlyPromiseReturn,
      loadOnAction = LOAD_SITE_LIST,
      loadOffAction = LOAD_SITE_LIST,
      successAction = SUCCESS_SITE_LIST,
      failureAction = CLEAR_SITE_LIST,
      successMessage,
      failureMessage,
      ...request
    } = payload

    // включаем лоадер по необходимости
    if (loadOnAction) {
      yield put({ type: loadOnAction, payload: true })
    }

    const { psfs, licensee } = request
    let partUrl = `${API_URL_SITE_LIST}${UrlHelper.getPsfsQuery({ psfs })}`

    if (licensee) {
      partUrl += `&licensee=${licensee}`
    }
    // собственно сам запрос на сервер
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl,
    })

    // проверяем наличие ошибки в ответе сервера
    const isError = Object.prototype.hasOwnProperty.call(response, 'error')

    // чекаем, актуальна ли опция "вернуть только промис (isOnlyPromiseReturn)"
    if (!isOnlyPromiseReturn) {
      if (isError) {
        // выполняем основное действие
        yield put({ type: failureAction })

        // выводим сообщение об ошибке, если оно необходимо
        if (successMessage) {
          yield put({
            type: SHOW_NOTICE,
            notice: NoticeHelper.createErrorObj(failureMessage),
          })
        }
      } else {
        // выполняем основное действие
        yield put({ type: successAction, listData: response })

        // выводим сообщение об успехе, если оно необходимо
        if (successMessage) {
          yield put({
            type: SHOW_NOTICE,
            notice: NoticeHelper.createSuccessObj(successMessage),
          })
        }
      }
    }

    // выключаем лоадер по необходимости
    if (loadOffAction) {
      yield put({ type: loadOffAction, payload: false })
    }

    // возвращаем фолсный промис, если он был какого-то хера передан
    if (isError && reject) {
      return yield call(reject, response)
    }

    // возвращаем трушный промис, если он был какого-то хера передан
    if (!isError && resolve) {
      return yield call(resolve, response)
    }
  } catch (error) {
    console.log('SITE INTERFACE ERROR', error)
  }
}

function* siteFetchListByUser(action) {
  try {
    const { licensee } = action.payload
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: licensee
        ? UrlHelper.create(API_URL_SITE_LIST_BY_SUPER_USER, {
            licensee,
          })
        : API_URL_SITE_LIST_BY_USER,
    })

    yield put({ type: SITE_FETCH_LIST_BY_USER_SUCCESS, listData: response })
  } catch (error) {
    console.log('SITE INTERFACE ERROR', error)
  }
}

function* fetchSiteTable(action) {
  try {
    yield put({ type: LOAD_SITE_TABLE, payload: true })

    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: `${API_URL_SITE_TABLE}${UrlHelper.getPsfsQuery(action.payload)}`,
    })

    if (Object.prototype.hasOwnProperty.call(response, 'error')) {
      return yield put({ type: CLEAR_SITE_TABLE })
    }
    yield put({ type: SUCCESS_SITE_TABLE, tableData: response })

    yield put({ type: LOAD_SITE_TABLE, payload: false })
  } catch (error) {
    console.log('SITE INTERFACE ERROR', error)
  }
}

function* fetchSiteForm({ payload }) {
  try {
    yield put({ type: LOAD_SITE_FORM, payload: true })

    const { entityId } = payload
    const formData = createSiteForm()

    formData.siteTypes = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: API_URL_DASHBOARD_SITE_TYPES,
    })

    formData.siteGroups = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: ApiRouter.SITE_GROUPS,
    })

    if (entityId) {
      formData.site = yield call(fetchFromAPI, {
        headers: getDefaultAutorizedHeaders(),
        partUrl: UrlHelper.create(API_URL_SITE_ITEM, {
          id: entityId,
        }),
      })
    }

    if (
      Object.prototype.hasOwnProperty.call(formData.siteTypes, 'error') ||
      Object.prototype.hasOwnProperty.call(formData.siteGroups, 'error') ||
      (formData.site &&
        Object.prototype.hasOwnProperty.call(formData.site, 'error'))
    ) {
      yield put({ type: CLEAR_SITE_FORM })
    } else {
      yield put({ type: SUCCESS_SITE_FORM, payload: formData })
    }

    yield put({ type: LOAD_SITE_FORM, payload: false })
  } catch (error) {
    console.log('SITE INTERFACE ERROR', error)
  }
}

function* siteChange(action) {
  try {
    const { entityId, values } = action.payload
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      method: !entityId ? 'POST' : 'PUT',
      partUrl: !entityId
        ? API_URL_SITE_LIST
        : UrlHelper.create(API_URL_SITE_ITEM, { id: entityId }),
      data: values,
    })

    if (response.error) {
      return yield call(action.promise.resolve, response.errors)
    }

    yield put({
      type: SHOW_NOTICE,
      notice: NoticeHelper.createSuccessObj(
        `Site ${entityId === 0 ? 'create' : 'change'} successful`,
      ),
    })

    return yield call(action.promise.resolve, false)
  } catch (error) {
    throw new Error(`UNKNOW SITE CHANGE ERROR. DATA: ${error}`)
  }
}

function* siteDelete(action) {
  try {
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      method: 'DELETE',
      partUrl: UrlHelper.create(API_URL_SITE_ITEM, {
        id: action.payload.entityId,
      }),
    })

    if (response === 204) {
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createSuccessObj('Site delete successful'),
      })

      return yield fetchSiteTable(action)
    }

    console.log('SITE DELETE UNKNOWN ERROR. RESPONSE: ', response)
  } catch (error) {
    console.log('SITE INTERFACE ERROR', error)
  }
}

function* siteSaga() {
  yield takeLatest(SITE_FETCH_LIST, siteFetchList)
  yield takeLatest(SITE_FETCH_LIST_BY_USER, siteFetchListByUser)
  yield takeLatest(FETCH_SITE_TABLE, fetchSiteTable)
  yield takeLatest(FETCH_SITE_FORM, fetchSiteForm)
  yield takeLatest(SITE_CHANGE, siteChange)
  yield takeLatest(SITE_DELETE, siteDelete)
}

export default siteSaga
